<template>
    <el-container style="height: calc(100vh - 80px)" class="bg-white">
        <el-header style="padding: 0px; height: auto; ">
            <!-- title部 -->
            <div class="h-12 p-4 shadow-md">システム設定 > 現場管理</div>

            <!-- 検索部 -->
            <div class="p-5">
                <div class="border border-gray-300 rounded-md p-4">
                    <el-form :model="searchForm" label-width="auto">
                        <div class="flex flex-col md:flex-row gap-x-4">
                            <el-form-item label="契約会社" class="md:w-80">
                                <CompanySelector v-model="searchForm.company_code" :isSearch=true :disabled="loginUserRole !== ROLES.SYSTEM"/>
                            </el-form-item>

                            <el-button class="w-24" type="primary"
                                @click="handleSearchButton">検索</el-button>
                        </div>
                    </el-form>

                    <div v-if="loginUserRole !== ROLES.GENERAL" class="flex justify-end">
                        <el-button class="w-24" type="primary"
                            @click="handleAddButton">新規登録</el-button>
                    </div>
                </div>
            </div>
        </el-header>

        <!-- 主显示部 -->
        <el-main style="padding-block: 0px;" v-loading="loading">
            <!-- テーブル -->
            <div class="border border-gray-300 rounded-md p-4">
                <el-table :data="mTableData" :height="loginUserRole !== ROLES.GENERAL ? 'calc(100vh - 380px)' :'calc(100vh - 350px)'">
                    <el-table-column label="編集" width="60" align="center">
                        <template #default="scope">
                            <el-button type="primary" link @click="handleEditButton(scope.row)">
                                <el-icon>
                                    <Edit />
                                </el-icon>
                            </el-button>
                        </template>
                    </el-table-column>
                    <el-table-column prop="site_code" label="現場コード" width="160"/>
                    <el-table-column prop="site_name" label="現場名" width="280"/>
                    <el-table-column prop="company_name" label="契約会社" width="280"/>
                    <el-table-column prop="site_address" label="現場住所" min-width="360"/>
                    <el-table-column prop="site_manager_name" label="安全責任者" width="200"/>
                    <el-table-column prop="site_manager_phone" label="責任者電話" width="160"/>
                    <el-table-column v-if="loginUserRole !== ROLES.GENERAL" label="削除" width="60" align="center">
                        <template #default="scope">
                            <el-button type="danger" link @click="handleDeleteButton(scope.row)">
                                <el-icon>
                                    <Delete />
                                </el-icon>
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <!-- ページネーション -->
            <div class="flex justify-end mt-2">
                <el-pagination v-model:current-page="mCurrentPage" v-model:page-size="mPageSize"
                    :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next, jumper" :total="mTotalRecords"
                    @size-change="handleSizeChange" @current-change="handleCurrentChange" />
            </div>
        </el-main>

        <el-footer style="height: 20px;"> </el-footer>
    </el-container>

    <!-- 新規登録・編集ダイアログ -->
    <el-dialog v-model="dialogFormVisible" :title="form.add_update === 'add' ? '現場新規登録' : '現場編集'" width="600px" align-center>
        <el-form :model="form" :rules="rules" ref="formRef" label-width="auto">

            <el-form-item label="現場コード" prop="site_code">
                <el-input v-model="form.site_code" :disabled="form.add_update === 'update'"
                    placeholder="現場コードを入力" />
            </el-form-item>

            <el-form-item label="現場名" prop="site_name">
                <el-input v-model="form.site_name" placeholder="現場名を入力" />
            </el-form-item>

            <el-form-item label="所属契約会社" prop="company_code">
                <CompanySelector v-model="form.company_code" :disabled="loginUserRole !== ROLES.SYSTEM"/>
            </el-form-item>

            <el-form-item label="現場住所" prop="site_address">
                <el-input v-model="form.site_address" placeholder="現場住所を入力" />
            </el-form-item>

            <el-form-item label="現場安全責任者" prop="site_manager">
                <UserSelector :company_code="form.company_code" v-model="form.site_manager" placeholder="責任者名を選択してください" :disabled="loginUserRole === ROLES.GENERAL"/>
            </el-form-item>
            
        </el-form>
        <template #footer>
            <div class="dialog-footer">
                <el-button @click="dialogFormVisible = false">キャンセル</el-button>
                <el-button type="primary" @click="handleAddUpdateSubmit(formRef)">確認</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script lang="ts">
import axios, { AxiosResponse } from 'axios';
import { ElMessage, ElMessageBox, ElPagination, ElTable, ElTableColumn } from 'element-plus';
import { defineComponent, onMounted, ref, nextTick } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { Delete, Edit } from '@element-plus/icons-vue';
import { ErrorCode, ResponseData, ResponseStatus } from '@/components/common/response';
import { getErrorMessage } from '@/components/common/errorMessage';
import { Operation } from '@/components/common/request';
import { SiteInfo } from '@/components/models/siteinfo';
import CompanySelector from '@/components/common/company-selector.vue';
import UserSelector from '@/components/common/user-selector.vue';
import { authService } from '@/services/authService';
import { ALL, ROLES } from '@/config/constants';
// import moment from 'moment';



export default defineComponent({
    name: 'MntSite',
    components: {
        CompanySelector,
        UserSelector,
        ElTable,
        ElTableColumn,
        ElPagination,
        Delete,
        // Plus,
        Edit,
        // Search,
        // Avatar
    },
    setup() {
        const loginUserRole = authService.getUserInfo().user_role ?? '';
        const loginUserCompanyCode = authService.getUserInfo().company_code ?? '';

        const searchForm = ref<any>({
            company_code: loginUserRole === ROLES.SYSTEM ? ALL : loginUserCompanyCode,
        });

        const form = ref<SiteInfo>({
            add_update: Operation.unknow,
            site_code: '',
            site_name: '',
            company_code: '',
            site_address: '',
            site_manager: '',
        });

        const loading = ref<boolean>(true);
        const mTableData = ref<SiteInfo[]>([]);
        const mTotalPages = ref<number>(0);
        const mTotalRecords = ref<number>(0);
        const mCurrentPage = ref<number>(1);
        const mPageSize = ref<number>(10);

        const size = ref<string>('small');
        const disabled = ref<boolean>(false);
        const background = ref<boolean>(true);

        const dialogFormVisible = ref<boolean>(false);

        const formRef = ref<FormInstance>()
        const rules = ref<FormRules>({
            site_code: [
                { required: true, message: '現場コードを入力してください', trigger: 'blur' },
                { min: 1, max: 10, message: '現場コードは1〜10文字の間で入力してください', trigger: 'blur' },
                { pattern: /^[A-Za-z0-9]+$/, message: '現場コードは半角英数字で入力してください', trigger: 'blur' }
            ],
            site_name: [
                { required: true, message: '現場名を入力してください', trigger: 'blur' },
                { min: 2, max: 25, message: '現場名は2〜25文字の間で入力してください', trigger: 'blur' },
                { pattern: /^[^<>\\/|&;$#%@"'{}()[\]]+$/, message: '現場名に使用できない文字が含まれています', trigger: 'blur' }
            ],
            company_code: [
                { required: true, message: '契約会社を選択してください', trigger: 'blur' }
            ],
            site_manager: [
                { required: true, message: '安全責任者を選択してください', trigger: 'blur' }
            ],
        })

        /***** マウント **************************/
        onMounted(() => {
            getSiteList();
        });

        /***** ボタン **************************/
        const handleSearchButton = () => {
            getSiteList();
        };

        const handleAddButton = () => {
            dialogFormVisible.value = true;
            nextTick(() => {
                formRef.value?.resetFields();
                form.value = {
                    add_update: Operation.add,
                    site_code: '',
                    site_name: '',
                    company_code: loginUserRole === ROLES.SYSTEM ? '' : loginUserCompanyCode,
                    company_name: '',
                    site_address: '',
                    site_manager: '',
                    site_manager_phone: '',
                };
            });
        };

        const handleEditButton = (row: SiteInfo) => {
            dialogFormVisible.value = true;
            nextTick(() => {
                formRef.value?.resetFields();
                form.value = {
                    add_update: Operation.update,
                    site_code: row.site_code,
                    site_name: row.site_name,
                    company_code: row.company_code,
                    company_name: row.company_name,
                    site_address: row.site_address,
                    site_manager: row.site_manager,
                    site_manager_phone: row.site_manager_phone,
                };
            });
        };

        const handleDeleteButton = (row: SiteInfo) => {
            ElMessageBox.confirm('削除しますか？', '削除', {
                confirmButtonText: '削除',
                cancelButtonText: 'キャンセル',
                type: 'warning',
            }).then(() => {
                deleteSite(row);
            }).catch(() => {
            });
        };


        const handleAddUpdateSubmit = async (formEl: FormInstance | undefined) => {
            if (!formEl) return
            await formEl.validate((valid) => {
                if (valid) {
                    if (form.value.add_update === Operation.add) {
                        addSite();
                    } else if (form.value.add_update === Operation.update) {
                        updateSite(form.value);
                    }
                    dialogFormVisible.value = false;
                }
            })
        }

        const handleSizeChange = (size: number) => {
            mPageSize.value = size;
            getSiteList();
        };
        const handleCurrentChange = (page: number) => {
            mCurrentPage.value = page;
            getSiteList();
        };

        /***** DB 操作 **************************/
        const apiCall = async (method: 'get' | 'post' | 'put' | 'delete', url: string, data?: any) => {
            try {
                loading.value = true;
                const res: AxiosResponse<ResponseData<SiteInfo[]>> = await axios({
                    method,
                    url,
                    data,
                    params: method === 'get' ? data : undefined, // Use params only for GET requests
                });

                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    if (method !== 'get') {
                        ElMessage.success(res.data.message);
                    }
                    return res.data;
                } else {
                    ElMessage.error(res.data.message);
                    return null;
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(error as Error));
            } finally {
                loading.value = false;
            }
        };

        const getSiteList = async () => {
            const data = await apiCall('get', '/api/sites/search', {
                page: mCurrentPage.value,
                limit: mPageSize.value,
                company_code: searchForm.value.company_code === ALL ? '' : searchForm.value.company_code,
            });
            if (data) {
                const { total, totalPages, currentPage, data: siteData } = data;
                mCurrentPage.value = currentPage!;
                mTotalPages.value = totalPages!;
                mTotalRecords.value = total!;
                mTableData.value = siteData!;
            }
            };

        const addSite = async () => {
            await apiCall('post', '/api/sites', form.value);
            getSiteList();
        };

        const updateSite = async (row: SiteInfo) => {
            await apiCall('put', `/api/sites/${row.site_code}`, row);
            getSiteList();
        };

        const deleteSite = async (row: SiteInfo) => {
            await apiCall('delete', `/api/sites/${row.site_code}`);
            getSiteList();
        };

        return {
            searchForm,
            form,
            dialogFormVisible,
            loading,
            mTableData,
            mCurrentPage,
            mPageSize,
            mTotalPages,
            mTotalRecords,
            size,
            disabled,
            background,
            handleSearchButton,
            handleAddButton,
            handleEditButton,
            handleDeleteButton,
            handleSizeChange,
            handleCurrentChange,
            handleAddUpdateSubmit,
            formRef,
            rules,
            loginUserRole,
            ROLES,
        }
    }
});
</script>